import AttributeModel, { IAttribute } from "../models/standard/AttributeModel";
import { IIfcObjectTag, IObjectLocalization } from "../models/standard/ObjectModel";
import { EAttributeType } from "./AttributeTypes";

/**
 * Interface that represents a part of the JSON object that gets returned when requesting products from the imsFilesApi.
 *
 * @param {number} id numerical identifier of the manufacturer.
 * @param {string} name displayed name of the manufacturer.
 */
export interface IManufacturer {
  id: number;
  name: string;
}

/**
 * Interface to represent a specific category inside of our imsFilesApi.
 *
 * @param {number} id numerical identifier for the category.
 * @param {string} name name of the category.
 */
 export interface IFilesApiObject {
  id: number;
  name: string;
  localization?: IObjectLocalization; 
  ifcObject?: IIfcObjectTag;  
  attributes: AttributeModel[];
}

/**
 * Enum to represent the different types of assets that can be connected to the product.
 */
export enum EAssetType {
  COLOR = "COLOR",
  MATERIAL = "MATERIAL",
  MODEL = "MODEL",
}

/**
 * Interface to represent a single asset of the product.
 *
 * @param {number} id numerical identifier of the asset.
 * @param {number} ownerId numerical identifier of the owner that created the asset.
 * @param {string} name string name of the asset.
 * @param {EAssetType} assetType the type of the asset.
 * @param {boolean} hasFullAccess flag to indicate if the user/product has full access to the asset.
 * @param {IFilesApiObject} object the category that this product belongs to.
 * @param {number} previewPictureId numerical identifier of the preview picture or null if non-existent.
 * @param {number} width width of the asset.
 * @param {number} height height of the asset.
 */
export interface IAsset {
  id: number;
  ownerId: number;
  name: string;
  assetType: EAssetType;
  hasFullAccess: boolean;
  object: IFilesApiObject;
  previewPictureId: number;
  width: number;
  height: number;
}

/**
 * Interface to describe the inner attribute object that is nested in an IAttribute object, which gets fetched from the database.
 *
 * @param {number} id index and identifier of the attribute inside of our database.
 * @param {number} ownerId identifier corresponding to the owner of this attribute.
 * @param {boolean} publicFlag boolean to differentiate between public attributes that can be shared between users and individual ones, which are restricted to the owner.
 * @param {string} key string name of the attribute.
 * @param {EAttributeType} type enum to describe the data type of the attribute.
 */
export interface IFilesApiAttributeData {
  id: number;
  ownerId: number;
  publicFlag: boolean;
  key: string;
  value: string;
  unit: string;
  type: EAttributeType;
}

/**
 * Interface that describes a single attribute of a given product.
 *
 * @param {number} id index and unique identifier for this attribute inside of our database.
 * @param {IFilesApiAttributeData} attribute the actual data contained within this attribute.
 * @param {string | null} valueText contains the value of the attribute, if the attribute is of type text or null if it is not.
 * @param {number | null} valueNumber contains the value of the attribute, if the attribute is of type number or null if it is not.
 * @param {boolean | null} valueBool contains the value of the attribute, if the attribute is of type boolean or null if it is not.
 */
export interface IFilesApiAttribute {
  id: number;
  attribute: IFilesApiAttributeData;
  valueText: string | null;
  valueNumber: number | null;
  valueBool: boolean | null;
}

/**
 * Interface describing a data object that can be part of a product.
 *
 * @param {number} id id of the data itself.
 * @param {number} ownerId id of the owner of the image.
 * @param {string} filePath string file path where the image is located on our server.
 * @param {string} fileName string file name of the originally uploaded image.
 * @param {string} fileFormat string representation which file format is being used.
 * @param {number} fileSize number representing the file size in bytes.
 * @param {string} creationDate UNIX time stamp when the image was uploaded and this object was created.
 * @param {string} modificationDate UNIX time stamp when the image was last edited.
 * @param {string} lowResPath string file path to the low resolution image.
 * @param {string} mediumResPath string file path to the medium resolution image.
 * @param {string} highResPath string file path to the high resolution image.
 * @param {string} thumbnailPath string file path to the thumbnail resolution image.
 */
export interface IData {
  id: number;
  ownerId: number;
  filePath: string;
  fileName: string;
  fileFormat: string;
  fileSize: number;
  creationDate: string;
  modificationDate: string;
  lowResPath: string;
  mediumResPath: string;
  highResPath: string;
  thumbnailPath: string;
}

/**
 * Interface representing the JSON object that gets returned when a product gets requested from the imsFilesApi.
 *
 * @param {number} id numerical identifier of the product.
 * @param {number} ownerId numerical identifier of the product owner.
 * @param {boolean} publicFlag boolean flag to indicate if this product will be publicly available to other users.
 * @param {IPreviewPicture} previewPicture the object containing all data related to the preview image.
 * @param {IAttribute[]} attributes IAttribute array containing all attributes that are associated with the product. Will default to an empty array.
 * @param {string} productId string identifier of the product.
 * @param {string} name string name of the product.
 * @param {string} description string description of the product.
 * @param {string} previewPicture string identifier of the preview picture for this product. Will default to null if non-existent.
 * @param {IManufacturer} manufacturer object representing the manufacturer of the product.
 * @param {boolean} isFavorite boolean flag to indicate if this product was favorited by the user.
 * @param {string[]} links an array containing possible links that are saved with the product.
 * @param {IAsset[]} assets array containing all the correlated assets of this product.
 * @param {IFilesApiObject[]} object the category object that this product belongs to.
 * @param {IData[]} data any json formatted array containing all related data of this product.
 */
export interface IFilesApiProduct {
  id: number;
  ownerId: number;
  publicFlag: boolean;
  previewPicture: IPreviewPicture;
  attributes: IAttribute[];
  productId: string;
  name: string;
  description: string;
  manufacturer: IManufacturer;
  isFavorite: boolean;
  links: string[];
  assets: IAsset[];
  objects: IFilesApiObject[];
  data: IData[];
  country: string;
}

/**
 * Interface that represents the JSON object that gets retrieved from the imsFilesApi when a user authenticates himself successfully.
 *
 * @param {string} accessToken the authentification token that was returned from the api.
 * @param {boolean} isAdmin boolean flag to indicate if the user is an admin or not.
 * @param {number} userId numerical user identification.
 */
export interface IFilesApiUser {
  accessToken: string;
  isAdmin: boolean;
  userId: number;
}

/**
 * Interface representing the preview image for a model or product.
 *
 * @param {number} id The unique identifier of the preview image.
 * @param {number} ownerId The identifier of the owner.
 * @param {string} filePath The path where the preview image is stored.
 * @param {string} fileName The file name of the preview image.
 * @param {string} fileFormat The image format (e.g., jpg, png).
 * @param {number} fileSize The size of the preview image in bytes.
 * @param {Date} creationDate The date when the image was created.
 * @param {Date | null} modificationDate The date when the image was last modified.
 * @param {string} lowResPath Path to the low-resolution version of the image.
 * @param {string} mediumResPath Path to the medium-resolution version of the image.
 * @param {string} highResPath Path to the high-resolution version of the image.
 * @param {string} thumbnailPath Path to the thumbnail version of the image.
 */
 export interface IPreviewPicture {
  id: number;
  ownerId: number;
  filePath: string;
  fileName: string;
  fileFormat: string;
  fileSize: number;
  creationDate: Date;
  modificationDate: Date | null;
  lowResPath: string;
  mediumResPath: string;
  highResPath: string;
  thumbnailPath: string;
}

/**
 * Interface representing a 2D model file that is part of a 3D model.
 *
 * @param {number} id The unique identifier of the 2D file.
 * @param {number} ownerId The identifier of the owner.
 * @param {string} filePath The file path to the 2D representation of this model.
 * @param {string} fileName The name of the file.
 * @param {string} fileFormat The file format (e.g., svg).
 * @param {number} fileSize The size of the file in bytes.
 * @param {Date} creationDate Date when this 2D file was created.
 * @param {Date | null} modificationDate Date of last modification (if any).
 */
 export interface IFile2D {
  id: number;
  ownerId: number;
  filePath: string;
  fileName: string;
  fileFormat: string;
  fileSize: number;
  creationDate: Date;
  modificationDate: Date | null;
}

/**
 * Interface representing a 3D model and its related assets from the imsFilesApi.
 *
 * @param {number} id The unique identifier of the model.
 * @param {number} ownerId The identifier of the model owner.
 * @param {boolean} publicFlag Indicates whether the model is publicly available.
 * @param {string} name The name of the model.
 * @param {EAssetType} assetType The asset type (expected to be "MODEL").
 * @param {IFilesApiObject} object The category that this model belongs to.
 * @param {string} filePath The file path where the 3D model is stored.
 * @param {string} icon The name of the associated icon.
 * @param {string} filename The file name used for the saved model.
 * @param {string} fileFormat The file extension of the model.
 * @param {number} fileSize The size of the model file in bytes.
 * @param {number} length The length dimension of the model.
 * @param {number} height The height dimension of the model.
 * @param {number} depth The depth dimension of the model.
 * @param {number} scale The scale factor of the model.
 * @param {Date} creationDate The creation timestamp of the model.
 * @param {Date | null} modificationDate The last modification timestamp (if any).
 * @param {IFile2D | null} file2d Optional 2D representation of the 3D model.
 * @param {IPreviewPicture | null} previewPicture Optional preview image for this model.
 */
export interface I3dModel {
  id: number;
  ownerId: number;
  publicFlag: boolean;
  name: string;
  assetType: EAssetType;
  object: IFilesApiObject;
  filePath: string;
  icon: string;
  filename: string;
  fileFormat: string;
  fileSize: number;
  length: number;
  height: number;
  depth: number;
  scale: number;
  creationDate: Date;
  modificationDate: Date | null;
  file2d?: IFile2D | null;
  rotation2D?: number;
  previewPicture?: IPreviewPicture | null;
}

/**
 * Interface describing the Material object that can be part of a product.
 *
 * @param {number} id the identifier of this material.
 * @param {number} ownerId the identifier of the owner of this material.
 * @param {boolean} publicFlag indicates if this material has been published yet.
 * @param {string} name the name of this material.
 * @param {EAssetType} assetType the asset type associated with this material. (= MATERIAL)
 * @param {IFilesApiObject} object the category of this material.
 * @param {IPreviewPicture} previewPicture the preview image for this material.
 * !TODO: Finish the rest of the interface documentation.
 */
export interface IMaterial {
  id: number;
  ownerID: number;
  publicFlag: boolean;
  name: string;
  assetType: EAssetType;
  objects: IFilesApiObject;
  previewPicture: IPreviewPicture;
  color: null;
  emissiveColor: null;
  emissiveIntensity: null;
  roughness: null;
  metalness: null;
  reflectivity: null;
  clearcoat: null;
  clearcoatRoughness: null;
  sheen: null;
  sheenRoughness: null;
  transmission: null;
  thickness: null;
  opacity: number;
  side: null;
  flatShading: null;
  blending: null;
  transparent: null;
  alphaTest: null;
  depthTest: null;
  depthWrite: null;
  width: number;
  length: number;
  bumpScale: null;
  normalScaleX: null;
  normalScaleY: null;
  aoMapIntensity: null;
  displacementScale: null;
  displacementBias: null;
  maps: IMaterialMap[];
}

/**
 * Enum describing the different material map types that a product material can have.
 */
export enum EMaterialMapType {
  bumpMap = "bump_map",
  map = "map",
  normalMap = "normal_map",
  roughnessMap = "roughness_map",
}

/**
 * Interface describing a map for a material that was fetched as an asset from one of the products from our files API.
 *
 * @author Daniel Rösch
 * @date 13.11.2024
 * @param {number} id the id of the material map.
 * @param {IPreviewPicture} image the preview picture object for the material map.
 * @param {EMaterialMapType} type the material map type.
 */
export interface IMaterialMap {
  id: number;
  image: IPreviewPicture;
  type: EMaterialMapType;
}

// INQUIRE: Types below needed?
/**
 * Type to ensure that the hex values for the colors from our API are configured correctly.
 */
export type HEX = `#${string}`;
/**
 * Type to ensure that the ral values of our colors are configured correctly.
 */
export type RAL = `RAL ${number}`;

/**
 * Interface describing a color that was fetched from our files API.
 *
 * @param {number} id the identifier of this color.
 * @param {number} ownerId the identifier of the owner of this color.
 * @param {string} name the name of this color.
 * @param {EAssetType} assetType the asset type associated with this color. (= COLOR)
 * @param {boolean} hasFullAccess indicates if the user has full access to the color.
 * @param {IFilesApiObject} objects the category of this color.
 * @param {HEX} hex the hexadecimal value of the color.
 * @param {RAL} ral the ral code for the color.
 */
export interface IFilesApiColor {
  id: number;
  ownerId: number;
  name: string;
  assetType: EAssetType;
  hasFullAccess: boolean;
  object: IFilesApiObject;
  hex: string;
  ral: string | null;
}

/**
 * Interface describing a material that was fetched from our files API.
 *
 * @param {number} id the identifier of this material.
 * @param {number} ownerId the identifier of the owner of this material.
 * @param {string} name the name of this material.
 * @param {EAssetType} assetType the asset type associated with this material. (= MATERIAL)
 * @param {boolean} hasFullAccess indicates if the user has full access to the material.
 * @param {IFilesApiObject} objects the category of this material.
 * @param {number} width the width of the material media.
 * @param {number} height the height of the material media.
 * @param {number | null} previewPictureId id of the preview picture if available, null otherwise.
 * @param {string} previewPictureUrl the url of the previewPicture if available, or empty string.
 */
export interface IFilesApiMaterial {
  id: number;
  ownerId: number;
  name: string;
  assetType: EAssetType;
  hasFullAccess: boolean;
  object: IFilesApiObject;
  length: number;
  width: number;
  previewPictureId: number | null;
  previewPictureUrl: string;
}

/**
 * Union type to combine the colors and materials for the texture drawer.
 */
export type TTexture = IFilesApiColor | IFilesApiMaterial;